import React, {Fragment} from 'react';

import {Button, Stack} from '@mui/material';
import {GetFranchisesTableModel} from 'domain/usecases';
import {FinishWithoutCompleteFlowEnum} from 'main/analytics/gaTagsEnum';
import {EmptySearchState, ErrorState, MainFooter, SentinelLoading} from 'presentation/components';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {useGetPaginatedItemsReactQuery} from 'presentation/pages/private/hooks/useGetPaginatedItemsReactQuery';
import {translations} from 'presentation/translations-new';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {SearchFilterHeader, TableComponent} from '../../components';
import {useFilters} from '../../hooks';
import {getFranchisesTable} from '../../services/getFranchisesTable';
import {StyledFooterContainer} from './styles';
import {FRANCHISES_TABLE_LIMIT} from './constants';

const {
	emptySearch: {noFranchisesFoundBySearchDescription},
} = translations['pt-br'].privateFlow.franchisesTable;
const {failToLoadTitle, failToLoadDescription, noResults, clearSearch} =
	translations['pt-br'].commons;

export function FranchisesTable() {
	const {isMobile} = useMediaQueries();
	const {shift, searchValueState, debouncedQueryFunc, clearSearchParams, abortDebounce} =
		useFilters();

	const {
		items,
		otherData,
		loadingItemsList,
		itemsListIsEmpty,
		itemsListHasError,
		loadMore,
		refresh,
	} = useGetPaginatedItemsReactQuery<GetFranchisesTableModel['grupoProcedimento'][0]>({
		queryKey: [QUERY_KEYS.FRANCHISES_TABLE, searchValueState, shift, FRANCHISES_TABLE_LIMIT],
		queryFn: ({page}) =>
			getFranchisesTable({
				nmProcedimento: searchValueState.toUpperCase() || undefined,
				cdGrupo: shift || undefined,
				limit: FRANCHISES_TABLE_LIMIT,
				page,
			}),
	});

	return (
		<Fragment>
			{!itemsListHasError ? (
				<SearchFilterHeader
					otherData={otherData}
					debouncedQueryFunc={debouncedQueryFunc}
					abortDebounce={abortDebounce}
					clearSearchParams={clearSearchParams}
				/>
			) : null}

			{items ? (
				<Fragment>
					<Stack margin={isMobile ? 2 : 4} gap={2}>
						<TableComponent grupos={items} />
					</Stack>
					<SentinelLoading
						minHeight="200px"
						onVisible={() => {
							if (items.length > 0) {
								loadMore();
							}
						}}
						isLoading={loadingItemsList}
					/>
				</Fragment>
			) : null}

			{itemsListIsEmpty && !itemsListHasError ? (
				<Stack sx={{height: '100%', p: 2}} alignItems="center" justifyContent="center">
					<EmptySearchState
						title={noResults}
						description={noFranchisesFoundBySearchDescription}
						dataTagId={FinishWithoutCompleteFlowEnum.SEM_FRANQUIA_ENCONTRADA}>
						<Button variant="contained" color="primary" onClick={clearSearchParams}>
							{clearSearch}
						</Button>
					</EmptySearchState>
				</Stack>
			) : null}

			{itemsListHasError ? (
				<ErrorState
					title={failToLoadTitle}
					description={failToLoadDescription}
					handleRefresh={refresh}
				/>
			) : null}

			<StyledFooterContainer $mb={63}>
				<MainFooter />
			</StyledFooterContainer>
		</Fragment>
	);
}
